import { stableStringify } from "@sail/utils";
import { Kind } from "graphql";
import { findEntryPointQuery, getFieldNodes } from "../graphql/documentUtils/entrypoint";
import { createQueryDocument } from "../graphql/documentUtils/query";
import { extractFragmentHashFromVariableName } from "./prefixes";
/**
 * Helper function used by v1 and v2 schemas so they can split queries based on
 * the values inside their respective contexts.
 * This is necessary because these variable values are used to set headers on the request,
 * so multiples can't co-exist.
 */ export function splitQueryByVariableValue(query, variables, variableSuffix, topLevelFields, operationType) {
    if (operationType === 'STANDALONE_QUERY') {
        return createSingleStandaloneQuery(query, variables, topLevelFields);
    }
    const uniqueVariableValuesToFragmentHashesMap = new Map();
    // Get a list of unique variable values with the given suffix
    Object.entries(variables).forEach(([key, value])=>{
        if (key.endsWith(variableSuffix) && typeof value === 'object' && value !== null) {
            const mapKey = stableStringify(value);
            const fragmentHash = extractFragmentHashFromVariableName(key);
            const existingFragmentHashes = uniqueVariableValuesToFragmentHashesMap.get(mapKey) || [];
            uniqueVariableValuesToFragmentHashesMap.set(mapKey, [
                ...existingFragmentHashes,
                fragmentHash
            ]);
        }
    });
    const queryEntryPoint = findEntryPointQuery(query);
    const fieldNodes = getFieldNodes(query);
    if (uniqueVariableValuesToFragmentHashesMap.size === 1) {
        return [
            {
                subQuery: query,
                variables,
                containsNonNullableFields: isAnyFieldNonNullable(fieldNodes, topLevelFields)
            }
        ];
    }
    const variableDefinitions = queryEntryPoint.variableDefinitions;
    if (typeof variableDefinitions === 'undefined' || variableDefinitions.length === 0) {
        throw new Error('No variable definitions found for query coming from composition query');
    }
    const attachedFragments = query.definitions.filter((definition)=>definition.kind === Kind.FRAGMENT_DEFINITION);
    const subqueryWithVariables = [];
    // Build each subquery based on the list of hashes that share unique variable values
    uniqueVariableValuesToFragmentHashesMap.forEach((hashList)=>{
        const subQueryVariables = groupVariablesByHashes(variables, hashList);
        const subQueryVariableDefinitions = groupVariableDefinitionsByHashes(variableDefinitions, hashList);
        const subQueryFieldNodes = groupFieldsByHashes(fieldNodes, hashList);
        const subQueryDocument = createQueryDocument(`${hashList.join('_')}Query`, subQueryVariableDefinitions, subQueryFieldNodes, attachedFragments);
        subqueryWithVariables.push({
            subQuery: subQueryDocument,
            variables: subQueryVariables,
            containsNonNullableFields: isAnyFieldNonNullable(subQueryFieldNodes, topLevelFields)
        });
    });
    return subqueryWithVariables;
}
function isAnyFieldNonNullable(fieldNodes, topLevelFields) {
    const fieldNames = fieldNodes.map((fieldNode)=>fieldNode.name.value);
    for (const name of fieldNames){
        const topLevelFieldMetadata = topLevelFields[name];
        if (typeof topLevelFieldMetadata !== 'undefined' && topLevelFieldMetadata.nonNullable) {
            return true;
        }
    }
    return false;
}
function groupFieldsByHashes(fieldNodes, hashList) {
    return fieldNodes.filter((fieldNode)=>fieldNodeAliasContainsAnyHash(fieldNode, hashList));
}
function groupVariableDefinitionsByHashes(variableDefinitions, hashList) {
    return variableDefinitions.filter((variableDefinition)=>hashList.some((hash)=>variableDefinition.variable.name.value.includes(hash)));
}
function groupVariablesByHashes(variables, hashList) {
    const variablesForHashList = {};
    Object.keys(variables).forEach((key)=>{
        if (hashList.some((hash)=>key.includes(hash))) {
            variablesForHashList[key] = variables[key];
        }
    });
    return variablesForHashList;
}
function fieldNodeAliasContainsAnyHash(fieldNode, hashes) {
    const alias = fieldNode.alias;
    if (typeof alias === 'undefined') {
        throw new Error(`Field ${fieldNode.name.value} coming from a composition query does not have an alias`);
    }
    return hashes.some((hash)=>alias.value.includes(hash));
}
function createSingleStandaloneQuery(query, variables, topLevelFields) {
    const fields = getFieldNodes(query);
    return [
        {
            subQuery: query,
            variables,
            containsNonNullableFields: isAnyFieldNonNullable(fields, topLevelFields)
        }
    ];
}

//# sourceMappingURL=splitQueryByVariableValue.js.map